import { Instagram, Youtube } from "lucide-react";
import { useEffect, useState } from "react";
import { FaFacebook, FaLinkedin, FaPinterest, FaTiktok } from "react-icons/fa6";
import { cn } from "../lib/utils";
import { PlatformType } from "../models/Platform";
import { getSignedUrl, isFirebaseStorageUrl } from "../services/Firebase";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";

interface PlatformConnectionIconProps {
  platform: PlatformType;
  profileImage?: string | null;
  className?: string;
  size?: "xs" | "sm" | "md" | "lg";
  profileName?: string;
  disableTooltip?: boolean;
  variant?: "composite" | "platform";
}

export function PlatformConnectionIcon({
  platform,
  profileImage,
  className,
  size = "md",
  profileName,
  disableTooltip = false,
  variant = "composite",
}: PlatformConnectionIconProps) {
  const [signedUrl, setSignedUrl] = useState<string | undefined>(undefined);

  useEffect(() => {
    const loadSignedUrl = async () => {
      if (
        profileImage &&
        (!profileImage.startsWith("http") || isFirebaseStorageUrl(profileImage))
      ) {
        try {
          const url = await getSignedUrl(profileImage);
          setSignedUrl(url);
        } catch (error) {
          console.error("Error loading profile image:", error);
        }
      }
    };
    loadSignedUrl();
  }, [profileImage]);

  const sizeClasses = {
    xs: "w-4 h-4",
    sm: "w-6 h-6",
    md: "w-8 h-8",
    lg: "w-10 h-10",
  };

  const iconSizes = {
    xs: "w-2 h-2",
    sm: "w-3 h-3",
    md: "w-4 h-4",
    lg: "w-5 h-5",
  };

  const getPlatformIcon = (platform: PlatformType) => {
    switch (platform) {
      case "instagram":
        return <Instagram className={cn("text-black", iconSizes[size])} />;
      case "youtube":
        return <Youtube className={cn("text-black", iconSizes[size])} />;
      case "tiktok":
        return <FaTiktok className={cn("text-black", iconSizes[size])} />;
      case "linkedin":
        return <FaLinkedin className={cn("text-black", iconSizes[size])} />;
      case "facebook":
        return <FaFacebook className={cn("text-black", iconSizes[size])} />;
      case "pinterest":
        return <FaPinterest className={cn("text-black", iconSizes[size])} />;
      default:
        return null;
    }
  };

  const IconContent =
    variant === "platform" ? (
      <div
        className={cn(
          "rounded-full border border-gray-200 bg-white flex items-center justify-center",
          sizeClasses[size],
          className
        )}
      >
        {getPlatformIcon(platform)}
      </div>
    ) : (
      <div className={cn("relative", sizeClasses[size], className)}>
        {/* Profile Image */}
        {profileImage ? (
          <img
            src={
              profileImage.startsWith("http") &&
              !isFirebaseStorageUrl(profileImage)
                ? profileImage
                : signedUrl || ""
            }
            alt={`${platform} profile`}
            className="w-full h-full rounded-full object-cover"
          />
        ) : (
          <div className="w-full h-full rounded-full bg-gray-100" />
        )}

        {/* Platform Icon Badge */}
        <div className="absolute bottom-0 right-0 bg-white rounded-full p-0.5 shadow-sm">
          {getPlatformIcon(platform)}
        </div>
      </div>
    );

  if (disableTooltip) {
    return IconContent;
  }

  // Helper function to format platform name safely
  const formatPlatformName = (p?: PlatformType): string => {
    if (!p) return "Unknown Platform"; // Handle undefined platform
    return p.charAt(0).toUpperCase() + p.slice(1);
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{IconContent}</TooltipTrigger>
        <TooltipContent>
          <p>
            {profileName || "No Name"} | {formatPlatformName(platform)}
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
